import crypto from 'crypto';

const PAID_MEDIUMS = new Set(['cpc', 'ppc', 'paid', 'paid_social']);
const PAID_SOCIAL_SOURCES = [
  'facebook',
  'meta',
  'instagram',
  'tiktok',
  'linkedin',
  'snapchat',
  'pinterest',
];

export const META_ATTRIBUTION_KEYS = [
  'fbclid',
  '_fbc',
  '_fbp',
  'fbc',
  'fbp',
] as const;

export const META_GRAPH_API_VERSION = 'v21.0';

export type MetaContact = {
  email?: string | null;
  phone?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  externalId?: string | null;
};

export type MetaConversionEvent = {
  event_name: string;
  event_time: number;
  event_id: string;
  action_source: 'website';
  user_data: Record<string, unknown>;
  custom_data?: Record<string, unknown>;
};

export function hashValue(value: string): string {
  return crypto.createHash('sha256').update(value).digest('hex');
}

export function hashEmail(email: string): string {
  if (!email) return '';

  let processedEmail = email.trim().toLowerCase();

  if (
    processedEmail.includes('@gmail.com') ||
    processedEmail.includes('@googlemail.com')
  ) {
    const [localPart, domain] = processedEmail.split('@');
    processedEmail = `${localPart.replace(/\./g, '')}@${domain}`;
  }

  return hashValue(processedEmail);
}

export function hashName(value: string): string {
  return hashValue(value.trim().toLowerCase());
}

export function formatPhoneToE164(phone: string): string {
  if (!phone) return '';

  let cleaned = phone.replace(/\D/g, '');

  if (cleaned.startsWith('0')) {
    cleaned = `44${cleaned.substring(1)}`;
  }

  if (!cleaned.startsWith('44') && !cleaned.startsWith('+')) {
    cleaned = `44${cleaned}`;
  }

  if (!cleaned.startsWith('+')) {
    cleaned = `+${cleaned}`;
  }

  return cleaned;
}

function normalizeString(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

export function hasAnyKey(
  obj: Record<string, unknown> | null | undefined,
  keys: readonly string[],
) {
  if (!obj) return false;
  return keys.some((key) => Object.prototype.hasOwnProperty.call(obj, key));
}

export function hasMetaAttribution(
  data: Record<string, unknown> | null | undefined,
): boolean {
  if (!data) return false;

  if (hasAnyKey(data, META_ATTRIBUTION_KEYS)) {
    return true;
  }

  const source = normalizeString(data.source ?? data.utm_source);
  const medium = normalizeString(data.utm_medium);

  if (PAID_SOCIAL_SOURCES.some((item) => source.includes(item))) {
    return true;
  }

  if (
    PAID_SOCIAL_SOURCES.some((item) => source.includes(item)) &&
    PAID_MEDIUMS.has(medium)
  ) {
    return true;
  }

  return false;
}

export function shouldSendMetaPurchase(
  attribution: Record<string, unknown> | null | undefined,
): boolean {
  if (!attribution) return false;

  return (
    hasMetaAttribution(attribution) ||
    hasAnyKey(attribution, ['_fbp', 'fbp']) ||
    Boolean(attribution.email)
  );
}

export function buildMetaUserData(
  attribution: Record<string, unknown>,
  contact: MetaContact = {},
): Record<string, unknown> {
  const userData: Record<string, unknown> = {};

  const fbc = attribution.fbc || attribution._fbc;
  const fbp = attribution.fbp || attribution._fbp;

  if (typeof fbc === 'string' && fbc) userData.fbc = fbc;
  if (typeof fbp === 'string' && fbp) userData.fbp = fbp;

  const email =
    contact.email ||
    (typeof attribution.email === 'string' ? attribution.email : undefined);
  const phone =
    contact.phone ||
    (typeof attribution.phone === 'string'
      ? attribution.phone
      : typeof attribution.phone_number === 'string'
        ? attribution.phone_number
        : undefined);
  const firstName =
    contact.firstName ||
    (typeof attribution.first_name === 'string'
      ? attribution.first_name
      : typeof attribution.name === 'string'
        ? attribution.name
        : undefined);
  const lastName =
    contact.lastName ||
    (typeof attribution.last_name === 'string'
      ? attribution.last_name
      : typeof attribution.surname === 'string'
        ? attribution.surname
        : undefined);

  if (email) userData.em = [hashEmail(email)];
  if (phone) userData.ph = [hashValue(formatPhoneToE164(phone))];
  if (firstName) userData.fn = [hashName(firstName)];
  if (lastName) userData.ln = [hashName(lastName)];

  const externalId =
    contact.externalId ||
    (typeof attribution.external_id === 'string'
      ? attribution.external_id
      : undefined);
  if (externalId) userData.external_id = [hashValue(externalId)];

  return userData;
}

export function buildMetaPurchaseEvent(options: {
  paymentId: string;
  stripeTransactionId?: string | null;
  enrollmentId: string;
  amountPence: number;
  convertedAt: string | Date;
  attribution: Record<string, unknown>;
  contact?: MetaContact;
}): MetaConversionEvent {
  const eventTime = Math.floor(new Date(options.convertedAt).getTime() / 1000);
  const stripeTransactionId = options.stripeTransactionId?.trim();

  return {
    event_name: 'Purchase',
    event_time: eventTime,
    event_id: stripeTransactionId || options.paymentId,
    action_source: 'website',
    user_data: buildMetaUserData(options.attribution, options.contact),
    custom_data: {
      currency: 'GBP',
      value: options.amountPence / 100,
      order_id: options.enrollmentId,
    },
  };
}

export function buildMetaLeadEvent(options: {
  leadId: string;
  createdAt: string | Date;
  attribution: Record<string, unknown>;
  contact?: MetaContact;
}): MetaConversionEvent {
  const eventTime = Math.floor(new Date(options.createdAt).getTime() / 1000);

  return {
    event_name: 'Lead',
    event_time: eventTime,
    event_id: options.leadId,
    action_source: 'website',
    user_data: buildMetaUserData(options.attribution, options.contact),
    custom_data: {
      currency: 'GBP',
      value: 1,
    },
  };
}

export async function uploadMetaConversionEvents(
  events: MetaConversionEvent[],
  options: {
    pixelId: string;
    accessToken: string;
  },
): Promise<Response | null> {
  if (events.length === 0) return null;

  const url = `https://graph.facebook.com/${META_GRAPH_API_VERSION}/${options.pixelId}/events?access_token=${options.accessToken}`;

  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ data: events }),
  });
}
